import { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ThemeToggle } from "./ThemeToggle";

// Pages ordered as they appear in the navbar (including dropdown items in order)
const NAV_PAGES = [
  "/",
  "/comparador",
  "/grups",
  "/novetats",
  "/histories",
  "/guia-models",
  "/guia-matricules",
  "/preferits",
  "/ultima-matricula",
  "/galeria",
];

export const Header = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  // isSidebarMounted: controls DOM presence; isSidebarVisible: controls CSS transform
  const [isSidebarMounted, setIsSidebarMounted] = useState(false);
  const [isSidebarVisible, setIsSidebarVisible] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [dropdownAlign, setDropdownAlign] = useState<"left" | "right">("left");
  const guiesDropdownRef = useRef<HTMLDivElement>(null);
  const extraDropdownRef = useRef<HTMLDivElement>(null);
  const DROPDOWN_WIDTH = 208; // w-52

  const openDropdownAt = (name: string, ref: React.RefObject<HTMLDivElement | null>) => {
    setOpenDropdown(name);
    const rect = ref.current?.getBoundingClientRect();
    if (!rect) return;
    const fitsLeftAligned = rect.left + DROPDOWN_WIDTH <= window.innerWidth - 8;
    setDropdownAlign(fitsLeftAligned ? "left" : "right");
  };

  // Keyboard navigation: press 1–8 to jump to navbar pages
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      const num = parseInt(e.key, 10);
      if (num >= 1 && num <= NAV_PAGES.length) {
        navigate(NAV_PAGES[num - 1]);
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [navigate]);

  const logoRef = useRef<HTMLAnchorElement>(null);
  const iniLinkRef = useRef<HTMLAnchorElement>(null);
  const hamburgerRef = useRef<HTMLButtonElement>(null);
  const [showCar, setShowCar] = useState(false);
  const [carStyle, setCarStyle] = useState<React.CSSProperties>({});

  useEffect(() => {
    if (sessionStorage.getItem("car-animated")) return;
    sessionStorage.setItem("car-animated", "1");

    const t = setTimeout(() => {
      const logo = logoRef.current;
      const iniLink = iniLinkRef.current;
      const hamburger = hamburgerRef.current;
      if (!logo) return;

      const logoRect = logo.getBoundingClientRect();
      const isMobile = window.innerWidth < 640;

      let targetX: number | null = null;
      if (isMobile && hamburger) {
        targetX = hamburger.getBoundingClientRect().left;
      } else if (!isMobile && iniLink) {
        targetX = iniLink.getBoundingClientRect().left;
      }
      if (targetX === null) return;

      const startX = logoRect.right + 6;
      const distance = targetX - startX;

      setCarStyle({
        position: "fixed",
        left: startX,
        top: logoRect.top + logoRect.height / 2,
        "--car-distance": `${distance}px`,
        animation: "carDrive 1.4s ease-in forwards",
        zIndex: 100,
        pointerEvents: "none",
        fontSize: "1.5rem",
        lineHeight: "1",
      } as React.CSSProperties);
      setShowCar(true);
      setTimeout(() => setShowCar(false), 1500);
    }, 200);
    return () => clearTimeout(t);
  }, []);

  const isActive = (path: string) => {
    if (path === "/" && location.pathname === "/") return true;
    if (path !== "/" && location.pathname.startsWith(path)) return true;
    return false;
  };

  const handleLinkClick = (path: string) => {
    setIsSidebarOpen(false);
    navigate(path);
  };

  // Open: mount → next frame → slide in. Close: slide out → unmount after 300ms
  useEffect(() => {
    if (isSidebarOpen) {
      setIsSidebarMounted(true);
      document.documentElement.style.overflow = 'hidden';
      // Two rAFs ensure the browser has painted translate-x-full before animating
      requestAnimationFrame(() => requestAnimationFrame(() => setIsSidebarVisible(true)));
    } else {
      setIsSidebarVisible(false);
      document.documentElement.style.overflow = '';
      const t = setTimeout(() => setIsSidebarMounted(false), 300);
      return () => clearTimeout(t);
    }
  }, [isSidebarOpen]);

  // Safety: always unmount on bfcache restore (Safari back/forward cache)
  useEffect(() => {
    const handlePageShow = (e: PageTransitionEvent) => {
      if (e.persisted) {
        setIsSidebarOpen(false);
        setIsSidebarMounted(false);
        setIsSidebarVisible(false);
        document.documentElement.style.overflow = '';
      }
    };
    window.addEventListener('pageshow', handlePageShow);
    return () => {
      window.removeEventListener('pageshow', handlePageShow);
      document.documentElement.style.overflow = '';
    };
  }, []);

  // Close sidebar on route change
  useEffect(() => {
    setIsSidebarOpen(false);
  }, [location.pathname]);

  return (
    <>
      <header className={`sticky top-0 bg-background border-b border-border shadow-sm transition-all ${
        isSidebarOpen ? 'z-40 sm:z-50' : 'z-50'
      }`}>
        <div className="container mx-auto px-4 sm:px-6 max-w-7xl">
          <div className="flex items-center justify-between h-16">
            {/* Logo/Title */}
            <Link
              ref={logoRef}
              to="/"
              className="flex items-center gap-2 hover:opacity-80 transition-opacity"
            >
              <img
                src="/logo-black.png"
                alt="Cotx.es"
                width={934}
                height={293}
                className="h-7 w-auto dark:hidden"
              />
              <img
                src="/logo-white.png"
                alt="Cotx.es"
                width={934}
                height={293}
                className="hidden h-7 w-auto dark:block"
              />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden sm:flex items-center gap-1 sm:gap-2">
              <Link
                ref={iniLinkRef}
                to="/"
                className={`px-3 sm:px-4 py-2 text-sm font-medium rounded-xl transition-all ${
                  isActive("/") && location.pathname === "/"
                    ? "bg-foreground text-background"
                    : "text-muted-foreground hover:bg-secondary hover:text-foreground"
                }`}
              >
                Inici
              </Link>
              <Link
                to="/comparador"
                className={`px-3 sm:px-4 py-2 text-sm font-medium rounded-xl transition-all ${
                  isActive("/comparador")
                    ? "bg-foreground text-background"
                    : "text-muted-foreground hover:bg-secondary hover:text-foreground"
                }`}
              >
                Comparador
              </Link>
              <Link
                to="/grups"
                className={`px-3 sm:px-4 py-2 text-sm font-medium rounded-xl transition-all ${
                  isActive("/grups")
                    ? "bg-foreground text-background"
                    : "text-muted-foreground hover:bg-secondary hover:text-foreground"
                }`}
              >
                Grups
              </Link>
              <Link
                to="/novetats"
                className={`px-3 sm:px-4 py-2 text-sm font-medium rounded-xl transition-all ${
                  isActive("/novetats")
                    ? "bg-foreground text-background"
                    : "text-muted-foreground hover:bg-secondary hover:text-foreground"
                }`}
              >
                Novetats
              </Link>
              <Link
                to="/histories"
                className={`px-3 sm:px-4 py-2 text-sm font-medium rounded-xl transition-all ${
                  isActive("/histories")
                    ? "bg-foreground text-background"
                    : "text-muted-foreground hover:bg-secondary hover:text-foreground"
                }`}
              >
                Històries
              </Link>
              
              <div className="w-px h-6 bg-border mx-1"></div>

              {/* Dropdown Guies */}
              <div
                ref={guiesDropdownRef}
                className="relative"
                onMouseEnter={() => openDropdownAt("guies", guiesDropdownRef)}
                onMouseLeave={() => setOpenDropdown(null)}
              >
                <button
                  className={`px-3 sm:px-4 py-2 text-sm font-medium rounded-xl transition-all flex items-center gap-1.5 ${
                    isActive("/guia-matricules") || isActive("/guia-models")
                      ? "bg-foreground text-background"
                      : "text-muted-foreground hover:bg-secondary hover:text-foreground"
                  }`}
                >
                  Guies
                  <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className={`transition-transform duration-200 opacity-60 ${openDropdown === "guies" ? "rotate-180" : ""}`}>
                    <polyline points="6 9 12 15 18 9"></polyline>
                  </svg>
                </button>
                {openDropdown === "guies" && (
                  <div className={`absolute top-[calc(100%+6px)] ${dropdownAlign === "left" ? "left-0" : "right-0"} w-52 bg-background border border-border rounded-2xl shadow-xl overflow-visible animate-in fade-in slide-in-from-top-1 duration-150`}>
                    {/* Transparent bridge covering the gap to prevent mouseLeave */}
                    <div className="absolute -top-[6px] inset-x-0 h-[6px]" />
                    <div className="p-1.5 overflow-hidden rounded-2xl">
                      <Link
                        to="/guia-models"
                        className={`flex items-center px-3.5 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                          isActive("/guia-models")
                            ? "bg-foreground text-background"
                            : "text-foreground hover:bg-secondary"
                        }`}
                      >
                        Guia Models
                      </Link>
                      <Link
                        to="/guia-matricules"
                        className={`flex items-center px-3.5 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                          isActive("/guia-matricules")
                            ? "bg-foreground text-background"
                            : "text-foreground hover:bg-secondary"
                        }`}
                      >
                        Guia Matrícules
                      </Link>
                    </div>
                  </div>
                )}
              </div>

              {/* Dropdown Extra */}
              <div
                ref={extraDropdownRef}
                className="relative"
                onMouseEnter={() => openDropdownAt("extra", extraDropdownRef)}
                onMouseLeave={() => setOpenDropdown(null)}
              >
                <button
                  className={`px-3 sm:px-4 py-2 text-sm font-medium rounded-xl transition-all flex items-center gap-1.5 ${
                    isActive("/endevina-preu") || isActive("/preferits") || isActive("/galeria") || isActive("/ultima-matricula")
                      ? "bg-foreground text-background"
                      : "text-muted-foreground hover:bg-secondary hover:text-foreground"
                  }`}
                >
                  Extra
                  <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className={`transition-transform duration-200 opacity-60 ${openDropdown === "extra" ? "rotate-180" : ""}`}>
                    <polyline points="6 9 12 15 18 9"></polyline>
                  </svg>
                </button>
                {openDropdown === "extra" && (
                  <div className={`absolute top-[calc(100%+6px)] ${dropdownAlign === "left" ? "left-0" : "right-0"} w-52 bg-background border border-border rounded-2xl shadow-xl overflow-visible animate-in fade-in slide-in-from-top-1 duration-150`}>
                    {/* Transparent bridge covering the gap to prevent mouseLeave */}
                    <div className="absolute -top-[6px] inset-x-0 h-[6px]" />
                    <div className="p-1.5 overflow-hidden rounded-2xl">
                      <Link
                        to="/endevina-preu"
                        className={`flex items-center px-3.5 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                          isActive("/endevina-preu")
                            ? "bg-foreground text-background"
                            : "text-foreground hover:bg-secondary"
                        }`}
                      >
                        Endevina el preu
                      </Link>
                      <Link
                        to="/preferits"
                        className={`flex items-center px-3.5 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                          isActive("/preferits")
                            ? "bg-foreground text-background"
                            : "text-foreground hover:bg-secondary"
                        }`}
                      >
                        Cotxes preferits
                      </Link>
                      <Link
                        to="/ultima-matricula"
                        className={`flex items-center px-3.5 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                          isActive("/ultima-matricula")
                            ? "bg-foreground text-background"
                            : "text-foreground hover:bg-secondary"
                        }`}
                      >
                        Última matrícula
                      </Link>
                      <Link
                        to="/galeria"
                        className={`flex items-center px-3.5 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                          isActive("/galeria")
                            ? "bg-foreground text-background"
                            : "text-foreground hover:bg-secondary"
                        }`}
                      >
                        Galeria
                      </Link>
                    </div>
                  </div>
                )}
              </div>

              <div className="w-px h-6 bg-border mx-1"></div>

              <ThemeToggle />
            </nav>

            {/* Mobile Hamburger Button */}
            <button
              ref={hamburgerRef}
              onClick={() => setIsSidebarOpen(!isSidebarOpen)}
              className="hamburger-button sm:hidden p-2 rounded-lg hover:bg-secondary transition-colors"
              aria-label="Obrir menú"
              type="button"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className={`transition-transform duration-300 ${isSidebarOpen ? 'rotate-90' : ''}`}
              >
                {isSidebarOpen ? (
                  <>
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </>
                ) : (
                  <>
                    <line x1="3" y1="12" x2="21" y2="12" />
                    <line x1="3" y1="6" x2="21" y2="6" />
                    <line x1="3" y1="18" x2="21" y2="18" />
                  </>
                )}
              </svg>
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Sidebar Overlay — only in DOM when sidebar is mounted */}
      {isSidebarMounted && (
        <div
          className={`fixed inset-0 bg-black/30 backdrop-blur-md z-50 sm:hidden transition-opacity duration-300 cursor-pointer ${
            isSidebarVisible ? 'opacity-100' : 'opacity-0 pointer-events-none'
          }`}
          onClick={() => setIsSidebarOpen(false)}
          onTouchStart={() => setIsSidebarOpen(false)}
          role="button"
          tabIndex={-1}
          aria-label="Tancar menú"
        />
      )}

      {/* Car animation */}
      <style>{`
        @keyframes carDrive {
          0%   { transform: translateY(-58%) translateX(0px) scaleX(-1); opacity: 1; }
          65%  { opacity: 1; }
          100% { transform: translateY(-58%) translateX(var(--car-distance)) scaleX(-1); opacity: 0; }
        }
      `}</style>
      {showCar && <span style={carStyle} aria-hidden="true">🚗</span>}

      {/* Mobile Sidebar — only in DOM when mounted, never on initial load */}
      {isSidebarMounted && <aside
        className={`sidebar fixed top-0 right-0 h-full h-[100dvh] w-64 bg-background shadow-xl z-[60] transform transition-transform duration-300 ease-in-out ${
          isSidebarVisible ? 'translate-x-0' : 'translate-x-full'
        }`}
        aria-hidden={!isSidebarVisible}
      >
        <div className="flex flex-col h-full">
          {/* Sidebar Header */}
          <div className="shrink-0 flex items-center justify-between px-6 py-4 border-b border-border">
            <span className="text-xl font-bold text-foreground">Menú</span>
            <button
              onClick={() => setIsSidebarOpen(false)}
              className="p-2 rounded-lg hover:bg-secondary transition-colors"
              aria-label="Tancar menú"
              type="button"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>

          {/* Sidebar Navigation */}
          <nav className="flex-1 min-h-0 overflow-y-auto overscroll-contain p-4 space-y-1">
            <button
              onClick={() => handleLinkClick("/")}
              className={`w-full text-left px-4 py-2.5 rounded-xl font-medium transition-all ${
                isActive("/") && location.pathname === "/"
                  ? "bg-foreground text-background"
                  : "text-muted-foreground hover:bg-secondary hover:text-foreground"
              }`}
            >
              Inici
            </button>
            <button
              onClick={() => handleLinkClick("/comparador")}
              className={`w-full text-left px-4 py-2.5 rounded-xl font-medium transition-all ${
                isActive("/comparador")
                  ? "bg-foreground text-background"
                  : "text-muted-foreground hover:bg-secondary hover:text-foreground"
              }`}
            >
              Comparador
            </button>
            <button
              onClick={() => handleLinkClick("/grups")}
              className={`w-full text-left px-4 py-2.5 rounded-xl font-medium transition-all ${
                isActive("/grups")
                  ? "bg-foreground text-background"
                  : "text-muted-foreground hover:bg-secondary hover:text-foreground"
              }`}
            >
              Grups automobilístics
            </button>
            <button
              onClick={() => handleLinkClick("/novetats")}
              className={`w-full text-left px-4 py-2.5 rounded-xl font-medium transition-all ${
                isActive("/novetats")
                  ? "bg-foreground text-background"
                  : "text-muted-foreground hover:bg-secondary hover:text-foreground"
              }`}
            >
              Novetats
            </button>
            <button
              onClick={() => handleLinkClick("/histories")}
              className={`w-full text-left px-4 py-2.5 rounded-xl font-medium transition-all ${
                isActive("/histories")
                  ? "bg-foreground text-background"
                  : "text-muted-foreground hover:bg-secondary hover:text-foreground"
              }`}
            >
              Històries
            </button>
            
            <div className="my-2 border-t border-border"></div>

            <button
              onClick={() => handleLinkClick("/guia-models")}
              className={`w-full text-left px-4 py-2.5 rounded-xl font-medium transition-all ${
                isActive("/guia-models")
                  ? "bg-foreground text-background"
                  : "text-muted-foreground hover:bg-secondary hover:text-foreground"
              }`}
            >
              Guia Models
            </button>
            <button
              onClick={() => handleLinkClick("/guia-matricules")}
              className={`w-full text-left px-4 py-2.5 rounded-xl font-medium transition-all ${
                isActive("/guia-matricules")
                  ? "bg-foreground text-background"
                  : "text-muted-foreground hover:bg-secondary hover:text-foreground"
              }`}
            >
              Guia Matrícules
            </button>

            <div className="my-2 border-t border-border"></div>

            <button
              onClick={() => handleLinkClick("/endevina-preu")}
              className={`w-full text-left px-4 py-2.5 rounded-xl font-medium transition-all ${
                isActive("/endevina-preu")
                  ? "bg-foreground text-background"
                  : "text-muted-foreground hover:bg-secondary hover:text-foreground"
              }`}
            >
              Endevina el preu
            </button>
            <button
              onClick={() => handleLinkClick("/preferits")}
              className={`w-full text-left px-4 py-2.5 rounded-xl font-medium transition-all ${
                isActive("/preferits")
                  ? "bg-foreground text-background"
                  : "text-muted-foreground hover:bg-secondary hover:text-foreground"
              }`}
            >
              Cotxes preferits
            </button>
            <button
              onClick={() => handleLinkClick("/ultima-matricula")}
              className={`w-full text-left px-4 py-2.5 rounded-xl font-medium transition-all ${
                isActive("/ultima-matricula")
                  ? "bg-foreground text-background"
                  : "text-muted-foreground hover:bg-secondary hover:text-foreground"
              }`}
            >
              Última matrícula
            </button>
            <button
              onClick={() => handleLinkClick("/galeria")}
              className={`w-full text-left px-4 py-2.5 rounded-xl font-medium transition-all ${
                isActive("/galeria")
                  ? "bg-foreground text-background"
                  : "text-muted-foreground hover:bg-secondary hover:text-foreground"
              }`}
            >
              Galeria
            </button>
          </nav>
          
          {/* Theme Toggle - Bottom Right */}
          <div className="shrink-0 p-4 pb-[max(1rem,env(safe-area-inset-bottom))] border-t border-border">
            <div className="flex justify-end">
              <ThemeToggle />
            </div>
          </div>
        </div>
      </aside>}
    </>
  );
};
